'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Surface } from '#pplx/frontend/aether/components/Surface/index.js';
import { PortalPrimitive } from '#pplx/frontend/aether/primitives/PortalPrimitive/index.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { default as classnames, default as classNames } from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
const ANIM_TIME = 250;
export const Overlay = React.memo(function Overlay({ animationClassName, children, isOpen = false, transparent = false, canOutsideClickClose = true, onBeforeClose, onClose, onIsClosing, childVariant = 'default', center = true, shouldClose = false, disableAnimation = false, testId, removeScroll = true, shards, delayClose = true, // TODO: fix this, we're duplicating this delay close logic at the popover and overlay level which can lead to animation issues
variant = 'default', portalTarget, includeBlur = true, forceColorScheme, canEscapeClose = true, overlayClassName, }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { portalTargetElement: contextPortalTarget } = useAetherContext();
    const resolvedPortalTarget = shouldUseActivationOrder
        ? undefined
        : (portalTarget ?? contextPortalTarget ?? undefined);
    const divRef = useRef(null);
    const contentRef = useRef(null);
    const portalRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);
    const isClicking = useRef(false);
    const handleClose = useCallback((reason) => {
        if (isClosing || onBeforeClose?.() === false) {
            return;
        }
        if (delayClose) {
            setIsClosing(true);
            onIsClosing?.();
            setTimeout(() => {
                onClose?.(reason);
                setIsClosing(false);
            }, ANIM_TIME);
        }
        else {
            setIsClosing(true);
            onClose?.(reason);
            setIsClosing(false);
        }
    }, [isClosing, onBeforeClose, delayClose, onIsClosing, onClose]);
    useEffect(() => {
        if (shouldClose && !isClosing) {
            handleClose('closed');
        }
    }, [shouldClose, isClosing, handleClose]);
    const handleBackdropClick = useCallback((e) => {
        if (!canOutsideClickClose) {
            // Honour the prop for both transparent popovers and opaque modals.
            // Transparent overlays still need stopPropagation so the click
            // doesn't fall through to the parent item handler; opaque ones
            // simply shouldn't close.
            if (transparent) {
                e.stopPropagation();
            }
            return;
        }
        if (e.target === divRef.current) {
            handleClose('backdrop');
        }
    }, [transparent, canOutsideClickClose, handleClose]);
    const handleDocumentClick = useCallback((e) => {
        const target = e.target;
        if (target instanceof Element) {
            if (contentRef.current === target || contentRef.current?.contains(target)) {
                // Only act as a click away listener if the click came from outside the content
                return;
            }
            const maybePortal = target.closest('[data-type="portal"]');
            if (maybePortal && maybePortal !== portalRef.current) {
                // But don't act as a click away listener if the click came from another overlay
                // portal--this overlay content opened an overlay above and we want to remain open
                // The exception here is for transparent overlays (like popovers), where we want to allow closing
                if (!transparent) {
                    return;
                }
            }
        }
        setIsClosing(false);
        handleClose('clickaway');
    }, [handleClose, transparent]);
    useEffect(() => {
        const hasDocumentClick = isOpen && transparent && canOutsideClickClose;
        if (!hasDocumentClick) {
            return;
        }
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [isOpen, transparent, handleDocumentClick, canOutsideClickClose]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && canEscapeClose && !e.defaultPrevented) {
            handleClose('escape');
        }
    }, [handleClose, canEscapeClose]);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        // Cleanup method
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);
    const animationClass = useMemo(() => classNames('duration-200', {
        'animate-in fade-in zoom-in-[0.98] ease-in fill-mode-backwards': ['default', 'large', 'small', 'hide-chrome', 'full-sheet', 'medium', 'thin'].includes(childVariant) && !isClosing,
        'zoom-out-[0.98] fade-out animate-out ease-out fill-mode-forwards': ['default', 'hide-chrome', 'full-sheet', 'large', 'small', 'medium', 'thin'].includes(childVariant) && isClosing,
        'fixed bottom-0 left-0': childVariant === 'bottom-left-sheet',
        'fixed top-0 inset-x-0': childVariant === 'side-sheet',
        'fixed bottom-0 inset-x-0': childVariant === 'bottom-sheet',
    }, animationClassName), [animationClassName, childVariant, isClosing]);
    const overlayBackgroundClassName = classNames('items-stretch md:items-center', {
        'fill-mode-both fixed inset-0': !transparent,
        'bg-backdrop': variant === 'default' || variant === 'default-less-blur',
        'bg-lightbox/95': variant === 'lightbox',
        'animate-in fade-in ease-outExpo duration-200': !isClosing && !disableAnimation,
        'animate-out fade-out ease-inExpo duration-200': isClosing && !disableAnimation,
    }, {
        'backdrop-blur-md': includeBlur && variant === 'lightbox',
        'backdrop-blur-xs': includeBlur && variant === 'default',
        'backdrop-blur-[0.5px]': includeBlur && variant === 'default-less-blur',
    }, overlayClassName);
    const portalChildren = useMemo(() => {
        if (!isOpen) {
            return null;
        }
        return (_jsxs(RemoveScroll, { removeScrollBar: false, enabled: removeScroll, allowPinchZoom: true, shards: shards, children: [_jsx("div", { className: overlayBackgroundClassName }), _jsx("div", { ref: divRef, onPointerDown: (e) => {
                        // If the source of the click is on the backdrop, register a
                        // close action
                        isClicking.current = e.target === divRef.current;
                    }, onPointerUp: (e) => {
                        if (isClicking.current) {
                            handleBackdropClick(e);
                        }
                        isClicking.current = false;
                    }, className: classnames({
                        'fixed inset-0 overflow-y-auto': !transparent,
                        'flex items-center justify-center': center,
                    }, overlayClassName), "data-test-id": testId, children: _jsx("div", { ref: contentRef, className: animationClass, children: children }) })] }));
    }, [
        isOpen,
        children,
        transparent,
        center,
        testId,
        handleBackdropClick,
        animationClass,
        contentRef,
        divRef,
        removeScroll,
        shards,
        overlayBackgroundClassName,
        overlayClassName,
    ]);
    if (shouldUseActivationOrder && !isOpen) {
        return null;
    }
    return (_jsx(PortalPrimitive, { targetElement: resolvedPortalTarget, children: _jsx(Surface, { children: _jsx("div", { ref: portalRef, "data-type": "portal", "data-color-scheme": forceColorScheme, children: portalChildren }) }) }));
});
//# sourceMappingURL=Overlay.js.map